import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { FacilitiesService, Facility, FacilityPagedApiResponse, FilterType } from 'src/app/handweave-api';
import { ApiConfig } from '../../api-config/api-config';

export interface SelectFacilitiesData {
  selected?: Facility[],
}

@Component({
  selector: 'app-select-facilities',
  templateUrl: './select-facilities.component.html',
  styleUrls: ['./select-facilities.component.css'],
  standalone: false
})
export class SelectFacilitiesComponent implements OnInit {
  selectedFacilities: Facility[] = [];

  private readonly pageSize: number = 10;
  tenantId: string = ApiConfig.tenantId || "unknown";
  lastResponse?: FacilityPagedApiResponse = null;
  loadError: boolean = false;

  searchControl = new FormControl<string>('');
  get searchQuery(): string {
    if (this.searchControl.value && this.searchControl.value.trim().length > 0) {
      return this.searchControl.value;
    } else {
      return null;
    }
  }

  constructor(
    private readonly dialogRef: MatDialogRef<SelectFacilitiesComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: SelectFacilitiesData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly facilitiesApi: FacilitiesService
  ) { 
    if (!this.data) this.data = { };
    this.selectedFacilities = [].concat(data.selected || []);
  }

  ngOnInit() {
    // Debounce search and trigger the request
    this.searchControl.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.lastResponse = null;
      this.fetchFacilities(1);
    });

    this.fetchFacilities(1);
  }

  fetchFacilities(requestPage: number) {
    this.loadError = false;
    this.lastResponse = null;
    let rx: Observable<FacilityPagedApiResponse>;

    // different API depending on whether we're searching or not
    if (this.searchQuery?.trim()?.length) {
      rx = this.facilitiesApi.facilitiesGetBySearch({
        tenantId: this.tenantId,
        page: requestPage,
        pageSize: this.pageSize,
        search: {
          searches: [{
            oneOf: [{
              field: 'name',
              value: `name:*${this.searchQuery}*`,
              filterType: FilterType.QueryString
            }]
          }]
        }
      });
    } else {
      rx = this.facilitiesApi.facilitiesGetItems({
        tenantId: this.tenantId,
        page: requestPage,
        pageSize: this.pageSize
      });
    }

    rx.subscribe({
      next: (r) => {
        this.lastResponse = r;
      },
      error: () => {
        this.loadError = true;
      }
    });
  }

  onPageChange(page: number) {
    this.fetchFacilities(page);
  }

  onSelectedChange(facility: Facility) {
    const index = this.selectedFacilities.findIndex(f => f.id === facility.id);
    if (index === -1) {
      this.selectedFacilities.push(facility);
    } else {
      this.selectedFacilities.splice(index, 1);
    }
  }

  isSelected(facility: Facility): boolean {
    if (!facility) return false;
    return this.selectedFacilities.findIndex(f => f.id === facility.id) > -1;
  }

  onDoneSelecting() {
    this.searchControl.setValue(null);
    const data: SelectFacilitiesData = { selected: this.selectedFacilities, }
    this.dialogRef.close(data);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

}
